import { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Paper, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, MenuItem, Select, InputLabel, FormControl,
  IconButton, Alert, Snackbar,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

const AdminSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  role: Yup.string().oneOf(['admin', 'super_admin'], 'Invalid role').required('Role is required')
});

export default function AdminsManagement() {
  const { currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllAdmins();
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch admins list',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreateAdmin = async (values, { resetForm, setSubmitting }) => {
    try {
      await adminAPI.registerAdmin(values);
      setSnackbar({
        open: true,
        message: 'Admin created successfully',
        severity: 'success'
      });
      setCreateDialogOpen(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to create admin',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRole = (admin) => {
    setSelectedAdmin(admin);
    setNewRole(admin.role);
    setEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedAdmin || !newRole) return;

    try {
      await adminAPI.changeAdminRole(selectedAdmin.id, { role: newRole });
      setSnackbar({
        open: true,
        message: 'Admin role updated successfully',
        severity: 'success'
      });
      setEditRoleDialogOpen(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin role:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update admin role',
        severity: 'error'
      });
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    try {
      await adminAPI.deleteAdmin(adminId);
      setSnackbar({
        open: true,
        message: 'Admin deleted successfully',
        severity: 'success'
      });
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete admin',
        severity: 'error'
      });
    }
  };

  const columns = [
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'firstName', headerName: 'First Name', width: 150 },
    { field: 'lastName', headerName: 'Last Name', width: 150 },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value === 'super_admin' ? 'Super Admin' : 'Admin'} 
          color={params.value === 'super_admin' ? 'primary' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { 
      field: 'lastLogin', 
      headerName: 'Last Login', 
      width: 180,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'yyyy-MM-dd HH:mm') : 'Never'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => {
        // Don't allow admins to modify super_admins unless they're super_admin themselves
        const canModify = 
          currentUser?.role === 'super_admin' || 
          params.row.role !== 'super_admin';
        
        // Don't allow admins to modify themselves
        const isSelf = params.row.id === currentUser?.id;
        
        return (
          <Box>
            <IconButton 
              onClick={() => handleEditRole(params.row)} 
              disabled={!canModify || isSelf}
              color="primary"
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={() => handleDeleteAdmin(params.row.id)} 
              disabled={!canModify || isSelf}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Only super admins can access this page
  if (currentUser?.role !== 'super_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access this page. Super Admin role required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Admins Management
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />} 
          onClick={() => setCreateDialogOpen(true)}
        >
          Add Admin
        </Button>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={1} sx={{ height: 500, width: '100%' }}>
          <DataGrid 
            rows={admins} 
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
          />
        </Paper>
      )}
      
      {/* Create Admin Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Admin</DialogTitle>
        <Formik
          initialValues={{
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            role: 'admin'
          }}
          validationSchema={AdminSchema}
          onSubmit={handleCreateAdmin}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  name="email"
                  label="Email"
                  fullWidth
                  margin="normal"
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                />
                <Field
                  as={TextField}
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                />
                <Field
                  as={TextField}
                  name="firstName"
                  label="First Name"
                  fullWidth
                  margin="normal"
                  error={touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                />
                <Field
                  as={TextField}
                  name="lastName"
                  label="Last Name"
                  fullWidth
                  margin="normal"
                  error={touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Field
                    as={Select}
                    name="role"
                    label="Role"
                    error={touched.role && !!errors.role}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Field>
                  {touched.role && errors.role && (
                    <Typography variant="caption" color="error">
                      {errors.role}
                    </Typography>
                  )}
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Admin'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      
      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={() => setEditRoleDialogOpen(false)}>
        <DialogTitle>Change Admin Role</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Change role for admin: <strong>{selectedAdmin?.email}</strong>
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateRole} 
            variant="contained" 
            color="primary"
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
